import { Link } from 'react-router-dom';
import { MoreHorizontal, Users, CheckSquare, Archive, Trash2, Settings, Star, LogOut } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useToggleFavorite } from '@/hooks/use-projects';
import { cn } from '@/lib/utils';
import { useI18n } from '@/lib/i18n';
import type { Project, ProjectRole } from '@/lib/schemas';

interface ProjectCardProps {
    project: Project;
    onArchive?: (projectId: string) => void;
    onDelete?: (projectId: string) => void;
    onLeave?: (projectId: string, memberId: string) => void;
}

const roleLabels: Record<ProjectRole, string> = {
    OWNER: 'Owner',
    ADMIN: 'Admin',
    MEMBER: 'Member',
    VIEWER: 'Viewer',
};

const roleBadgeColors: Record<ProjectRole, string> = {
    OWNER: 'bg-amber-500/20 text-amber-400',
    ADMIN: 'bg-purple-500/20 text-purple-400',
    MEMBER: 'bg-blue-500/20 text-blue-400',
    VIEWER: 'bg-gray-500/20 text-gray-400',
};

export function ProjectCard({ project, onArchive, onDelete, onLeave }: ProjectCardProps) {
    const { t } = useI18n();
    const canManage = project.role === 'OWNER' || project.role === 'ADMIN';
    const canDelete = project.role === 'OWNER';
    const canLeave = project.role !== 'OWNER'; // Non-owners can leave
    const toggleFavorite = useToggleFavorite();

    // Translated role labels
    const roleLabelsTranslated: Record<ProjectRole, string> = {
        OWNER: t('projects.owner'),
        ADMIN: t('projects.admin'),
        MEMBER: t('projects.member'),
        VIEWER: t('projects.viewer'),
    };

    const handleToggleFavorite = (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        toggleFavorite.mutate(project.id);
    };

    return (
        <Card className="group relative overflow-hidden transition-all hover:border-primary/50">
            <Link
                to={`/projects/${project.id}`}
                className="block p-4"
            >
                {/* Header */}
                <div className="flex items-start gap-3">
                    {project.imageUrl ? (
                        <img
                            src={project.imageUrl}
                            alt={project.name}
                            className="h-12 w-12 rounded-lg object-cover flex-shrink-0"
                        />
                    ) : (
                        <div
                            className="h-12 w-12 rounded-lg flex items-center justify-center text-2xl flex-shrink-0"
                            style={{ backgroundColor: project.color || '#6366f1' }}
                        >
                            {project.iconEmoji || '📁'}
                        </div>
                    )}
                    <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-foreground truncate group-hover:text-primary transition-colors">
                            {project.name}
                        </h3>
                        {project.description && (
                            <p className="text-sm text-foreground-muted line-clamp-2 mt-1">
                                {project.description}
                            </p>
                        )}
                    </div>
                </div>

                {/* Stats */}
                <div className="flex items-center gap-4 mt-4 text-sm text-foreground-muted">
                    <div className="flex items-center gap-1.5">
                        <Users className="h-4 w-4" />
                        <span>{project.memberCount}</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                        <CheckSquare className="h-4 w-4" />
                        <span>{project.taskCount} {t('projectCard.tasks')}</span>
                    </div>
                </div>

                {/* Role badge */}
                <div className="mt-3">
                    <span
                        className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${roleBadgeColors[project.role]
                            }`}
                    >
                        {roleLabelsTranslated[project.role]}
                    </span>
                </div>
            </Link>

            {/* Favorite button */}
            <Button
                variant="ghost"
                size="icon"
                className={cn(
                    "absolute top-3 left-3 h-8 w-8 transition-opacity",
                    project.isFavorite ? "opacity-100" : "opacity-0 group-hover:opacity-100"
                )}
                onClick={handleToggleFavorite}
                disabled={toggleFavorite.isPending}
            >
                <Star
                    className={cn(
                        "h-4 w-4",
                        project.isFavorite ? "fill-amber-500 text-amber-500" : "text-foreground-muted"
                    )}
                />
            </Button>

            {/* Actions dropdown */}
            {(canManage || canLeave) && (
                <div className="absolute top-3 right-3">
                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8 opacity-0 group-hover:opacity-100 transition-opacity"
                                onClick={(e) => e.preventDefault()}
                            >
                                <MoreHorizontal className="h-4 w-4" />
                            </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                            {canManage && (
                                <>
                                    <DropdownMenuItem asChild>
                                        <Link
                                            to={`/projects/${project.id}/settings`}
                                            className="flex items-center gap-2"
                                        >
                                            <Settings className="h-4 w-4" />
                                            {t('projectCard.settings')}
                                        </Link>
                                    </DropdownMenuItem>
                                    <DropdownMenuItem
                                        onClick={(e) => {
                                            e.preventDefault();
                                            onArchive?.(project.id);
                                        }}
                                    >
                                        <Archive className="h-4 w-4 mr-2" />
                                        {project.isArchived ? t('projectCard.unarchive') : t('projectCard.archive')}
                                    </DropdownMenuItem>
                                </>
                            )}
                            {canDelete && (
                                <>
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem
                                        className="text-destructive focus:text-destructive"
                                        onClick={(e) => {
                                            e.preventDefault();
                                            onDelete?.(project.id);
                                        }}
                                    >
                                        <Trash2 className="h-4 w-4 mr-2" />
                                        {t('projectCard.delete')}
                                    </DropdownMenuItem>
                                </>
                            )}
                            {canLeave && (
                                <>
                                    {canManage && <DropdownMenuSeparator />}
                                    <DropdownMenuItem
                                        className="text-destructive focus:text-destructive"
                                        onClick={(e) => {
                                            e.preventDefault();
                                            onLeave?.(project.id, project.membershipId || '');
                                        }}
                                    >
                                        <LogOut className="h-4 w-4 mr-2" />
                                        {t('projectCard.leaveProject')}
                                    </DropdownMenuItem>
                                </>
                            )}
                        </DropdownMenuContent>
                    </DropdownMenu>
                </div>
            )}

            {/* Archived indicator */}
            {project.isArchived && (
                <div className="absolute top-3 left-12 bg-surface-hover px-2 py-0.5 rounded text-xs text-foreground-muted">
                    {t('projectCard.archived')}
                </div>
            )}
        </Card>
    );
}
